import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  useSidebar,
} from '@/components/ui/sidebar';

interface CollapsibleNavMainItemProps {
  item: {
    title: string;
    url: string;
    icon?: React.ElementType;
    isActive?: boolean;
    onClick?: (e: React.MouseEvent) => void | Promise<void>;
    items?: {
      title: string;
      url: string;
      isActive?: boolean;
      onClick?: (e: React.MouseEvent) => void | Promise<void>;
    }[];
  };
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
}

export function CollapsibleNavMainItem({ item, isOpen, onToggle, onNavigate }: CollapsibleNavMainItemProps) {
  const { state } = useSidebar();

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onToggle();
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <div className="relative flex items-center">
        <SidebarMenuButton
          isActive={item.isActive}
          tooltip={item.title}
          aria-label={item.title}
          className="w-full justify-start pl-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!pl-2"
          onClick={(e) => {
            // Allow navigation if URL is not a placeholder
            if (item.url !== '#') {
              onNavigate(item.url);
            }
            // If it's a collapsible section, the chevron handles the toggle
          }}
        >
          {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
          <span className="group-data-[collapsible=icon]:hidden truncate">{item.title}</span>
        </SidebarMenuButton>
        {state !== 'collapsed' && (
          <CollapsibleTrigger asChild>
            <button
              aria-label={`Toggle ${item.title} section`}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-sidebar-accent"
              onClick={handleTriggerClick}
            >
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
              />
            </button>
          </CollapsibleTrigger>
        )}
      </div>
      <CollapsibleContent>
        <SidebarMenuSub>
          {item.items?.map((subItem, subIndex) => (
            <SidebarMenuItem key={subIndex}>
              <SidebarMenuSubButton
                asChild
                isActive={subItem.isActive}
                className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  if (subItem.onClick) {
                    subItem.onClick(e);
                  } else if (subItem.url !== '#') {
                    onNavigate(subItem.url);
                  }
                }}
              >
                <button>{subItem.title}</button>
              </SidebarMenuSubButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}
